//  ==========================================================================
//
//  Join Our Team
//
//  ==========================================================================
import React from 'react';

import { graphql } from 'gatsby';

import {
	MetaTags,
	Section,
	SectionIntro,
	Stat,
	StatWrapper,
	GridStandard,
	HeroTitleOverlay,
	TextLink
} from '../components';
import WordsbyImg from '../components/WordsbyImg';
import { allRoutes, normalizeCuratedContent } from '../helpers';
import { css } from 'emotion';
import styled from 'react-emotion';
import { mq } from '../global-styles';
import { ImageBreaker, VideoBreaker, ImageBreakerContent, ImageBreakerOverlayContent } from '../components/Breaker';


//  ==========================================================================
//  Styles
//  ==========================================================================
const StyledGlassdoorWrap = styled( 'div' )`
	padding: 0 11px;

	${mq.sm( css`
		margin-left: auto;
		margin-right: auto;
		max-width: 620px;
		width: 82%;
	` )};

	${mq.lg( css`
		max-width: 1130px;
		padding: 0;
	` )};
`;

const StyledImage = styled( WordsbyImg )`
	background-color: transparent;
	picture {
		background-color: transparent;
	}
`;

const StyledSection = styled( Section )`
	margin-bottom: 70px;
	${mq.md(
		css`
			margin-bottom: 90px;
		`
	)};
	${mq.xl(
		css`
			margin-bottom: 120px;
		`
	)};
`;

const StyledBreakerContentTextLink = styled( TextLink )`
    padding: 3px 0;
    color: white;
    margin-top: 15px;

    ${mq.md( css`
		margin-top: 30px;

		&.text-align-right {
			text-align: right;
			margin-right: 15px;
		}
		&.text-align-center {
			text-align: center;
		}
		&.text-align-left {
			text-align: left;
			margin-left: 15px;
		}
    ` )};
    
	> span {
		${mq.md(
		css`
                color: white !important;
                white-space: pre-wrap;
			`
	)};
	}
`;

const nomalizeCuratedStories = ( props, position ) => {
	let stories = [];
	let count = 0;
	props.data.page.editor_blocks.map( ( block ) => {
		if ( 'hks-blocks/curated-posts' === block.name ) {
			count++;
			if ( count === position ) {
				stories = block.attributes?.selectedPosts.map(
					( { relationshipByID } ) =>
						normalizeCuratedContent( relationshipByID )
				);
			}
		}
	} );

	return stories;
};

//  ==========================================================================
//  Component
//  ==========================================================================
const JoinOurTeam = props => {
	
	const joinOurTeamData = {
		description:
			props.data.allWordpressWpStaticPageMeta.edges[0].node
				.join_our_team_meta_desc,
		jumpLinks: [
			{
				name: 'People First',
				path: '#people-first',
			},
			{
				name: 'Public Interest Design',
				path: '#public-interest-design',
			},
			{
				name: 'Xchange Program',
				path: '#spin-the-globe',
			},
			{
				name: 'Fellowship',
				path: '#fellowship',
			},
			{
				name: 'What\'s It Like to Work with Us?',
				path: '#glassdoor',
			},
		],
		ctaLinks: [
			{
				name: 'See Careers',
				path: allRoutes.external_careers_us_global,
			},
			{
				name: 'See Internships',
				path: allRoutes.external_internship,
			},
		],
		benefits: [
			{
				name: 'Flexible Work',
				description: 'We empower you to excel at your job while making room for your personal life. Our firm offers opportunities to log in from home in addition to working from the office.',
			},
			{
				name: 'Time Off',
				description: 'When you work hard, you need time to recharge. We want you to go on that getaway or hit the pause button to take care of your family using our paid time-off options.',
			},
			{
				name: 'Professional Growth',
				description: 'We lead with knowledge. HKS supports your career development with continuing education programs and allowances for licenses, certifications and professional memberships.',
			},
			{
				name: 'Health and Well-being',
				description: 'Your physical and mental well-being are important to us. Our benefits package includes comprehensive medical coverage and other resources for you to manage your health.',
			},
			{
				name: 'Family Care',
				description: 'Parents, we’re here for you. Whether you’re welcoming a child through birth or adoption, paid parental leave is available to eligible employees.',
			},
			{
				name: 'Festive Culture',
				description: 'We love to celebrate milestones, yours and ours. HKS hosts a flurry of parties and events throughout the year to recognize our employees and to treat our friends and families.',
			},
		],
		yiselleImage:
			props.data.yiselleImage?.childImageSharp?.fluid,
		citizcnklsImage: props.data.citizcnklsImage?.childImageSharp?.fluid,
		xchangeProgramImage:
			props.data.xchangeProgramImage?.childImageSharp?.fluid,
		fellowshipImage:
			props.data.fellowshipImage?.childImageSharp?.fluid,
		CitizenHKSLogo:
			props.data.CitizenHKSLogo?.childImageSharp?.fluid,
		curatedStories1: nomalizeCuratedStories( props, 1 ),
		curatedStories2: nomalizeCuratedStories( props, 2 ),
		curatedStories3: nomalizeCuratedStories( props, 3 ),
	};

	const {xchangeProgramVideo, citizcnklsVideo} = props.data;

	const {
		description,
		yiselleImage,
		xchangeProgramImage,
		citizcnklsImage,
		fellowshipImage,
		CitizenHKSLogo,
		curatedStories1,
		curatedStories2,
		curatedStories3,
		ctaLinks,
		jumpLinks,
		benefits,
	} = joinOurTeamData;

	const { post_title, hero } = props.data.page;
	
	return (
		<main id="main">
			<MetaTags post_title={post_title} description={description} />

			<HeroTitleOverlay
				hero={hero}
				post_title={post_title}
				description="We want you for you: your professional talents, yes, but also your personal interests, your perspectives, your aspirations, your voice. That’s why we invest in initiatives to help you grow and benefits that acknowledge what you care about. Join us and make us better. Explore career opportunities at HKS through the links below."
				ctaLinks={ctaLinks}
				jumpLinks={jumpLinks}
			/>

			<div className="content-contain">
				<Section post_title="people-first">
					<div className="content-contain__interior-lvl-one-to-two">
						<SectionIntro
							withHero={true}
							post_title="People First"
							description="Our firm recognizes that different backgrounds and vantage points can enrich our work and our relationships with clients beyond what conventional skills alone can accomplish. We’re committed to making HKS – and the design industry as a whole – more reflective of the communities we serve by making our workforce more diverse and by knocking down barriers to joining and rising in the design profession."
						/>
					</div>
				</Section>
			</div>

			<ImageBreaker
				image={yiselleImage}
				imageAlt="yiselleImage speaking to a group of people"
			>
				<ImageBreakerContent
					position="right"
				>
					<p className={'type--c1 text-align-left'}>As a firm, we must look like our communities and clients in order to provide them the best quality product.</p>
					<StyledBreakerContentTextLink
						to="/our-news/articles/hks-names-new-director-of-equity-diversity-and-inclusion/"
						className="link--rolling-stone type--a7 display-block text-align-left"
						iconValue="link-arrow"
						iconSize="sm"
					>Read more about Yiselle's journey to becoming HKS' first Director of Equity, Diversity and Inclusion
					</StyledBreakerContentTextLink>
				</ImageBreakerContent>
			</ImageBreaker>

			{0 < curatedStories1.length && (
				<StyledSection post_title="curated-stories-1">
					<div className="content-contain">
						<GridStandard content={curatedStories1} />
					</div>
				</StyledSection>
			)}

			<div className="content-contain">
				<Section post_title="public-interest-design">
					<div className="content-contain__interior-lvl-one-to-two">
						<SectionIntro
							withHero={true}
							post_title="Public-Interest Design"
							description="Transform lives with us. Citizen HKS, our philanthropic initiative, connects our design expertise with communities that need it most, from a Chicago high school looking to better serve students with special needs to a Ugandan town with an outdated maternity ward. We partner with nonprofits, civic leaders, like-minded contractors and design industry peers to get projects built, outfitted and operational."
						/>
					</div>
				</Section>
			</div>

			<VideoBreaker
				video={citizcnklsVideo.publicURL}
				poster={citizcnklsImage}
				posterAlt="Wood peg wall"
			>
				<ImageBreakerOverlayContent
					position="center"
				>
					<StyledImage
						alt="CITIZEN HKS Logo"
						sizes={CitizenHKSLogo}
						attachment={CitizenHKSLogo}
						artDirectedSizes="(min-width: 768px) 100vw, 250vw"
						className="img"
						mobilePosition={'overlaid'}
					/>
				</ImageBreakerOverlayContent>
				<ImageBreakerContent
					position="center"
				>	
					<StyledBreakerContentTextLink
						to="https://www.hksinc.com/our-news/articles/citizen-hks-overview/"
						className="link--rolling-stone type--a7 display-block text-align-center"
						iconValue="link-arrow"
						iconSize="sm"
					>A Powerful Agent for Change. Learn More
					</StyledBreakerContentTextLink>
				</ImageBreakerContent>
			</VideoBreaker>

			{0 < curatedStories2.length && (
				<StyledSection post_title="curated-stories-2">
					<div className="content-contain">
						<GridStandard content={curatedStories2} />
					</div>
				</StyledSection>
			)}

			<div className="content-contain">
				<Section post_title="spin-the-globe">
					<div className="content-contain__interior-lvl-one-to-two">
						<SectionIntro
							withHero={true}
							post_title="Spin the Globe"
							description="With 24 offices worldwide, we find creative ways to build bridges among our teams. Every year, our Xchange Fellowship sends 12 full-time employees — architects, interior designers and other professionals — to one of three HKS host offices for a three-month stint. Xchange exposes promising staffers to new ideas, projects and work styles and connects them to colleagues they may know only electronically. "
						/>
					</div>
				</Section>
			</div>

			<VideoBreaker
				video={xchangeProgramVideo.publicURL}
				poster={xchangeProgramImage}
				posterAlt="Man holding onto train rail"
			>
				<ImageBreakerContent
					position="right"
				>
					<p className="type--c1 text-align-left">{'It\'s an opportunity that not a lot of people get in their career and it’s worth stepping out of your comfort zone.'}</p>
					<StyledBreakerContentTextLink
						to="/our-news/articles/hks-xchange-fellowship/"
						className="link--rolling-stone type--a7 display-block text-align-left"
						iconValue="link-arrow"
						iconSize="sm"
					>Read more about The Xchange Program at HKS
					</StyledBreakerContentTextLink>
				</ImageBreakerContent>
			</VideoBreaker>
				
			<div className="content-contain">
				<Section post_title="fellowship">
					<div className="content-contain__interior-lvl-one-to-two">
						<SectionIntro
							withHero={true}
							post_title="Fellowships"
							description="We want our employees to be limitless thinkers, so we leave room outside of project work for our emerging talent to mine their curiosity. Our regional and firmwide design fellowships team up young professionals with our design and research leaders, offering them mentorship and freedom to explore their ideas. HKS, a worldwide leader in health care design, is also home to one of the industry’s most prestigious health design fellowships, offered to recent graduates committed to improving the experiences of patients and health care workers."
						/>
					</div>
				</Section>
			</div>


			<ImageBreaker
				image={fellowshipImage}
				imageAlt="Interconnected strings"
			>
				<ImageBreakerContent
					position="left"
				>
					<p className={'type--c1 text-align-left'}>{'My mentors were very much focused on making sure I was doing what I wanted to do and not trying to push me into some pre-designed objective.'}</p>
					<StyledBreakerContentTextLink
						to="/our-news/articles/kick-starting-your-career-the-hks-health-fellowship/"
						className="link--rolling-stone type--a7 display-block text-align-left"
						iconValue="link-arrow"
						iconSize="sm"
					>Read more about jump starting your career with the Health Fellowships
					</StyledBreakerContentTextLink>
				</ImageBreakerContent>
			</ImageBreaker>

			{0 < curatedStories3.length && (
				<StyledSection post_title="curated-stories-3">
					<div className="content-contain">
						<GridStandard content={curatedStories3} />
					</div>
				</StyledSection>
			)}

			<Section post_title="glassdoor">
				<div className="content-contain">
					<StyledGlassdoorWrap>
						<SectionIntro
							post_title="What's It Like to Work with Us?"
							description="Just ask our team members past and present. We're an OpenCompany on Glassdoor, which means that we embrace transparent reviews."
							cta="View our Glassdoor Profile"
							link="https://www.glassdoor.com/Overview/Working-at-HKS-EI_IE24075.11,14.htm"
						/>
					</StyledGlassdoorWrap>

					<hr />

					<StyledGlassdoorWrap>
						<StatWrapper source="Stats pulled on November 18th, 2020. For the most up to date stats, visit our Glassdoor page.">
							<Stat stat="93%" label="Approve of CEO" />
							<Stat stat="87%" label="Recommend to a Friend" />
							<Stat stat="4.3" label="Total Glassdoor Rating" />
						</StatWrapper>
					</StyledGlassdoorWrap>

					<hr />
						
				</div>
			</Section>

			<Section post_title="benefits">
				<div className="content-contain">
					<div className="content-contain__interior-lvl-two">
						<GridStandard content={benefits} cardStyle="text" />
					</div>
				</div>
			</Section> 
				
		</main>
	);
	
};

export const joinOurTeamQuery = graphql`
	query JoinOurTeamQuery {
		page: wordsbyCollections(post_name: { eq: "join-our-team" }) {
			post_title
			...HeroComponent
			editor_blocks {
				...CuratedStoryEditorBlockFields
			}
		}
		allWordpressWpStaticPageMeta: allWordsbyCollections(
			filter: { post_type: { eq: "static_page_meta" } }
		) {
			edges {
				node {
					join_our_team_meta_desc
				}
			}
		}
		yiselleImage: file(
			relativePath: { regex: "/join-our-team-yiselle.png/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		citizcnklsImage: file(
			relativePath: { regex: "/join-our-team-citizcnkls.png/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		citizcnklsVideo: file(
			relativePath: { regex: "/SWH-hands-720.mp4/" }
		) {
			publicURL
		}
		xchangeProgramImage: file(
			relativePath: { regex: "/join-ou-team-xchange-program.png/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		xchangeProgramVideo: file(
			relativePath: { regex: "/xchange.mp4/" }
		) {
			publicURL
		}
		fellowshipImage: file(
			relativePath: { regex: "/join-our-team-fellowship.png/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
		CitizenHKSLogo: file(
			relativePath: { regex: "/CitizenHKS-logo.png/" }
		) {
			childImageSharp {
				fluid(maxWidth: 2880) {
					...GatsbyImageSharpFluid_noBase64
				}
			}
		}
	}
`;

export default JoinOurTeam;